import {
  BillGroupSummary,
  IContactRespSchema,
  IOneTimeInvoiceFormSchemaWithOfferings,
  LegalEntityAddressFormatEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useGetById } from '../../../../api/queryUtils';
import { OneTimeInvoiceContactsDrawer } from './OneTimeInvoiceContactsDrawer';

/** Matches the quote's contact types, minus the ones an invoice has no use for. */
export type OneTimeInvoiceContactType = 'primary' | 'billing';

interface OneTimeInvoiceContextValue {
  accountId: string;
  accountName: string;
  legalEntityName: string;
  /** Legal entity address format, used to render contact and account addresses. */
  addressFormat: Maybe<LegalEntityAddressFormatEnum>;
  currency: string;
  billGroups: BillGroupSummary[];
  isBillGroupsLoading: boolean;
  shippingContact: Maybe<IContactRespSchema>;
  billingContact: Maybe<IContactRespSchema>;
  handleChangeContact: (
    contact: IContactRespSchema,
    type: OneTimeInvoiceContactType,
  ) => void;
  /**
   * Opens the contacts drawer, which owns billing/shipping contact selection
   * and the invoice address source.
   */
  openContactsDrawer: () => void;
}

const OneTimeInvoiceContext = createContext<OneTimeInvoiceContextValue | null>(
  null,
);

export const useOneTimeInvoice = () => {
  const context = useContext(OneTimeInvoiceContext);
  if (!context) {
    throw new Error(
      'useOneTimeInvoice must be rendered within the OneTimeInvoiceProvider',
    );
  }
  return context;
};

interface OneTimeInvoiceProviderProps extends Omit<
  OneTimeInvoiceContextValue,
  | 'openContactsDrawer'
  | 'shippingContact'
  | 'billingContact'
  | 'handleChangeContact'
> {
  children: ReactNode;
}

/**
 * Carries the read-only account and bill group data the invoice form sections
 * need, resolves the selected contacts, and owns the contacts drawer so any
 * section can open it without an `isOpen` flag being drilled down the tree.
 * Form state itself comes from `FormProvider`, so this must be rendered inside
 * one.
 */
export const OneTimeInvoiceProvider = ({
  accountId,
  accountName,
  legalEntityName,
  addressFormat,
  currency,
  billGroups,
  isBillGroupsLoading,
  children,
}: OneTimeInvoiceProviderProps) => {
  const [isContactsDrawerOpen, setIsContactsDrawerOpen] = useState(false);
  const { setValue, watch } =
    useFormContext<IOneTimeInvoiceFormSchemaWithOfferings>();

  const openContactsDrawer = useCallback(
    () => setIsContactsDrawerOpen(true),
    [],
  );
  const closeContactsDrawer = useCallback(
    () => setIsContactsDrawerOpen(false),
    [],
  );

  const shippingContactId = watch('shippingContactId');
  const billingContactId = watch('billingContactId');

  // The form stores contact ids; the selects and address blocks need records.
  // Fetching by id rather than searching a page of account contacts keeps this
  // correct for accounts with more contacts than one page holds.
  const { data: shippingContact } = useGetById<IContactRespSchema>(
    'contacts',
    shippingContactId,
    { enabled: !!shippingContactId, refetchOnWindowFocus: false },
  );
  const { data: billingContact } = useGetById<IContactRespSchema>(
    'contacts',
    billingContactId,
    { enabled: !!billingContactId, refetchOnWindowFocus: false },
  );

  const handleChangeContact = useCallback(
    (contact: IContactRespSchema, type: OneTimeInvoiceContactType) => {
      setValue(
        type === 'primary' ? 'shippingContactId' : 'billingContactId',
        contact.id,
        { shouldDirty: true, shouldValidate: true },
      );
    },
    [setValue],
  );

  const value = useMemo(
    () => ({
      accountId,
      accountName,
      legalEntityName,
      addressFormat,
      currency,
      billGroups,
      isBillGroupsLoading,
      shippingContact,
      billingContact,
      handleChangeContact,
      openContactsDrawer,
    }),
    [
      accountId,
      accountName,
      legalEntityName,
      addressFormat,
      currency,
      billGroups,
      isBillGroupsLoading,
      shippingContact,
      billingContact,
      handleChangeContact,
      openContactsDrawer,
    ],
  );

  return (
    <OneTimeInvoiceContext.Provider value={value}>
      {children}

      {isContactsDrawerOpen && (
        <OneTimeInvoiceContactsDrawer onClose={closeContactsDrawer} />
      )}
    </OneTimeInvoiceContext.Provider>
  );
};
